import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { useNotifications } from '@/stores/notifications';

import type {
  ChatMessage,
  ChatResponse,
  FeedbackMessage,
  FeedbackResponse,
} from './chat';
import { sendChatMessage, sendFeedback } from './chat';
import type { ChatHistoryParams, ChatHistoryResponse } from './chat-history';
import { getChatHistory } from './chat-history';

// Query Keys for BhanzuBuddy
const bhanzuBuddyKeys = {
  all: ['bhanzu-buddy'] as const,
  chat: () => [...bhanzuBuddyKeys.all, 'chat'] as const,
  chatHistory: () => [...bhanzuBuddyKeys.all, 'chat-history'] as const,
  sendMessage: () => [...bhanzuBuddyKeys.chat(), 'send'] as const,
  sendFeedback: () => [...bhanzuBuddyKeys.chat(), 'feedback'] as const,
  getHistory: (params?: ChatHistoryParams) =>
    [...bhanzuBuddyKeys.chatHistory(), 'get', params] as const,
} as const;

// Get Chat History Query
const useGetChatHistory = ({
  params,
  onSuccess,
  onError,
  enabled,
}: {
  params: ChatHistoryParams;
  onSuccess?: (data: ChatHistoryResponse) => void;
  onError?: () => void;
  enabled?: boolean;
}) => {
  const { addNotification } = useNotifications();

  const query = useQuery({
    queryKey: bhanzuBuddyKeys.getHistory(params),
    queryFn: () => getChatHistory(params),
    enabled: enabled ?? !!params.lead_id,
  });

  // Handle success/error with useEffect since onSuccess/onError are deprecated
  React.useEffect(() => {
    if (query.isSuccess && query.data) {
      onSuccess?.(query.data);
    }
  }, [query.isSuccess, query.data, onSuccess]);

  React.useEffect(() => {
    if (query.isError) {
      onError?.();
    }
  }, [query.isError, addNotification, onError]);

  return query;
};

// Send Chat Message Mutation
const useSendChatMessage = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ChatResponse) => void;
  onError?: (error?: unknown) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    mutationFn: (data: ChatMessage) => sendChatMessage(data),
    mutationKey: bhanzuBuddyKeys.sendMessage(),
  });

  // Handle success/error with useEffect since onSuccess/onError are deprecated
  React.useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      // Invalidate chat history to refresh the list
      void queryClient.invalidateQueries({
        queryKey: bhanzuBuddyKeys.chatHistory(),
      });
      onSuccess?.(mutation.data);
    }
  }, [mutation.isSuccess, mutation.data, queryClient, onSuccess]);

  React.useEffect(() => {
    if (mutation.isError) {
      addNotification({
        title: 'Message Failed',
        message: 'Unable to send your message. Please try again.',
        type: 'error',
      });
      onError?.();
    }
  }, [mutation.isError, mutation.error, addNotification, onError]);

  return mutation;
};

// Send Feedback Mutation
const useSendFeedback = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: FeedbackResponse) => void;
  onError?: () => void;
} = {}) => {
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    mutationFn: (data: FeedbackMessage) => sendFeedback(data),
    mutationKey: bhanzuBuddyKeys.sendFeedback(),
  });

  React.useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      addNotification({
        title: 'Feedback Sent',
        message: 'Thank you for your feedback!',
        type: 'success',
      });
      onSuccess?.(mutation.data);
    }
  }, [mutation.isSuccess, mutation.data, addNotification, onSuccess]);

  React.useEffect(() => {
    if (mutation.isError) {
      addNotification({
        title: 'Feedback Failed',
        message: 'Unable to send your feedback. Please try again.',
        type: 'error',
      });
      onError?.();
    }
  }, [mutation.isError, addNotification, onError]);

  return mutation;
};

// Combined hook for BhanzuBuddy functionality
export const useBhanzuBuddy = () => {
  return {
    useGetChatHistory,
    useSendChatMessage,
    useSendFeedback,
  };
};
